import type { ListAs400ExportResponse } from '@erp/rpc';

export const ITEM_ADD_INDEX_PAGE_SIZE = 200;

export type ItemAddIndexPageInfo = {
  hasNextPage: boolean;
  totalPages?: number;
  totalCount?: number;
  totalCountExact?: boolean;
};

export function exactAs400PageCount(
  pageInfo: ItemAddIndexPageInfo,
): number | null {
  if (pageInfo.totalCountExact === false) return null;
  if (pageInfo.totalPages !== undefined) return pageInfo.totalPages;
  if (pageInfo.totalCount !== undefined) {
    return Math.ceil(pageInfo.totalCount / ITEM_ADD_INDEX_PAGE_SIZE);
  }
  return null;
}

export type FetchedIndexPage = {
  rows: readonly unknown[];
  pageInfo: ItemAddIndexPageInfo;
};

export function extraAs400Pages(pageInfo?: ItemAddIndexPageInfo): number {
  if (!pageInfo) return 0;
  const exact = exactAs400PageCount(pageInfo);
  if (exact !== null) return Math.max(0, exact - 1);
  return pageInfo.hasNextPage ? 1 : 0;
}

export function pageWalkedOffSweep(page: FetchedIndexPage): boolean {
  return (
    page.pageInfo.totalCountExact === false &&
    page.rows.length === 0 &&
    page.pageInfo.hasNextPage
  );
}

export function extraPagesFromFetched(
  later: readonly (FetchedIndexPage | undefined)[],
): number {
  const last = later.at(-1);
  if (!last || pageWalkedOffSweep(last)) return later.length;
  if (last.pageInfo.hasNextPage) return later.length + 1;
  return later.length;
}

export function extraAs400PageCount(
  first?: FetchedIndexPage,
  later: readonly (FetchedIndexPage | undefined)[] = [],
): number {
  if (later.length === 0) {
    if (!first || pageWalkedOffSweep(first)) return 0;
    return extraAs400Pages(first.pageInfo);
  }
  return extraPagesFromFetched(later);
}

export function itemAddIndexCoverageError(
  pages: readonly (FetchedIndexPage | undefined)[],
): Error | null {
  return pages.some((page) => page !== undefined && pageWalkedOffSweep(page))
    ? new Error('Item Add index is incomplete')
    : null;
}

export function mergeAs400ItemAddPages(
  pages: readonly (
    Pick<ListAs400ExportResponse, 'rows' | 'newRows'> | undefined
  )[],
): {
  rows: ListAs400ExportResponse['rows'];
  newRows: NonNullable<ListAs400ExportResponse['newRows']>;
} {
  return {
    rows: pages.flatMap((page) => page?.rows ?? []),
    newRows: pages[0]?.newRows ?? [],
  };
}

export function itemAddIndexView(
  first: {
    data?: ListAs400ExportResponse;
    error: unknown;
    isPending: boolean;
  },
  rest: readonly {
    data?: ListAs400ExportResponse;
    error: unknown;
    isPending: boolean;
  }[],
  extraSettled: boolean,
): {
  data:
    | {
        rows: ListAs400ExportResponse['rows'];
        newRows: NonNullable<ListAs400ExportResponse['newRows']>;
      }
    | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const error =
    queryError(first.error, rest) ??
    itemAddIndexCoverageError([first.data, ...rest.map((page) => page.data)]);
  if (error) return { data: undefined, isLoading: false, error };
  const complete =
    extraSettled &&
    first.data !== undefined &&
    rest.every((page) => page.data !== undefined);
  if (!complete) {
    return { data: undefined, isLoading: true, error: null };
  }
  return {
    data: mergeAs400ItemAddPages([
      first.data,
      ...rest.map((page) => page.data),
    ]),
    isLoading: false,
    error: null,
  };
}

function queryError(
  first: unknown,
  rest: readonly { error: unknown }[],
): Error | null {
  const raw = first ?? rest.find((page) => page.error)?.error;
  if (raw instanceof Error) return raw;
  if (typeof raw === 'string') return new Error(raw);
  return raw == null ? null : new Error('Item Add index failed');
}
